import React, { useCallback, useEffect, useState, useRef } from 'react';
import { styled } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import CheckboxIcon from '@atlaskit/icon/core/migration/checkbox-checked--checkbox';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Flex, xcss } from '@atlaskit/primitives';

import { fg } from '@confluence/feature-gating';

import { useIsDisabledContext } from '../IsDisabledContext';
import type { UnsplashResult } from '../index';

const ImagesWrapperStyles = xcss({
	marginTop: 'space.100',
	justifyContent: 'space-between',
	paddingTop: 'space.0',
	paddingRight: 'space.025',
	paddingLeft: 'space.025',
});

const baseImageStyles = {
	height: '44px',
	width: '46px',
	borderRadius: '4px',
	border: 'none',
};

const noImageStyles = {
	backgroundColor: 'transparent',
	borderWidth: '1px',
	borderStyle: 'solid',
	borderColor: token('color.border'),

	'&::before': {
		content: '',
		position: 'absolute',
		left: '50%',
		top: '5%',
		height: '90%',
		borderWidth: '1px',
		borderStyle: 'solid',
		borderColor: token('color.border'),
		rotate: '45deg',
	},
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImagePlaceholder = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...baseImageStyles,
	backgroundColor: token('color.background.neutral'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Image = styled.button<{
	imageSrc: string;
	isSelectedImage: boolean;
	isUploadingImage?: boolean;
	isNoImage?: boolean;
}>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isNoImage }) => ({
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		...baseImageStyles,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		cursor: `${({ isUploadingImage }) => (isUploadingImage ? 'auto' : 'pointer')}`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundImage: `${({ imageSrc }) => `url(${imageSrc})`}`,
		backgroundSize: 'cover',
		backgroundPosition: 'center',
		backgroundRepeat: 'no-repeat',
		position: 'relative',
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'flex-end',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		boxShadow: `${({ isSelectedImage }) =>
			isSelectedImage ? `inset 0 0 0 2px ${token('color.border.selected')}` : 'none'}`,

		'&:focus-visible': {
			outline: '2px solid',
			outlineColor: token('color.border.focused'),
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> span': {
			position: 'absolute',
			bottom: token('space.negative.075', '-6px'),
			right: token('space.negative.075', '-6px'),
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> span > svg': {
			width: '30px',
			height: '30px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		...(isNoImage && noImageStyles),
	}),
);

const i18n = defineMessages({
	noImageLabel: {
		id: 'custom-sites-extensions.unsplash-search.default-image-picker.no-image',
		defaultMessage: 'No image',
		description: 'Alt label for default unsplash pick with no image',
	},
});

export const renderImagePlaceholders = () => {
	return Array(5)
		.fill(null)
		.map((_, index) => <ImagePlaceholder key={index} data-testid="image-picker-placeholders" />);
};

type DefaultImagePicker = {
	imageAltText?: string;
	selectedImageUrl?: string;
	initialUnsplashResults: UnsplashResult[] | undefined;
	handleImageSelection: (selectedImage: UnsplashResult) => void;
	analyticsSource: string;
	hasNoImage: boolean;
	isUploadingImage?: boolean;
	enablePaddingBottom?: boolean;
	isNoImageAllowed?: boolean;
};

export const DefaultImagePicker = ({
	imageAltText,
	selectedImageUrl,
	handleImageSelection,
	initialUnsplashResults,
	analyticsSource,
	isUploadingImage,
	isNoImageAllowed,
	hasNoImage,
}: DefaultImagePicker) => {
	const [isLoading, setIsLoading] = useState<boolean>(!initialUnsplashResults?.length);
	const isDisabled = useIsDisabledContext();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();

	const onImageClick = useCallback(
		(unsplashResult) => {
			if (!isUploadingImage) {
				handleImageSelection(unsplashResult);

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'selected',
						actionSubject: 'image',
						source: analyticsSource,
						attributes: {
							imageSource: 'unsplashDefault',
						},
					},
				}).fire();
			}
		},
		[analyticsSource, createAnalyticsEvent, handleImageSelection, isUploadingImage],
	);

	const firstImage = useRef<UnsplashResult | undefined>();

	if (initialUnsplashResults?.length) {
		if (
			selectedImageUrl &&
			!initialUnsplashResults.map((result) => result.fullSizeImageUrl).includes(selectedImageUrl)
		) {
			firstImage.current = { fullSizeImageUrl: selectedImageUrl, altDescription: imageAltText };
		} else if (!fg('company_hub_manual_cards_refinement')) {
			firstImage.current = initialUnsplashResults[0];
		}
	}

	const defaultUnsplashImages: (Partial<UnsplashResult> | undefined)[] = [
		firstImage.current ?? initialUnsplashResults?.[0],
		initialUnsplashResults?.[1],
		initialUnsplashResults?.[2],
		initialUnsplashResults?.[3],
		initialUnsplashResults?.[4],
	];

	if (isNoImageAllowed && fg('company_hub_manual_cards_refinement')) {
		defaultUnsplashImages.splice(1, 0, {});
		defaultUnsplashImages.pop();
	}

	useEffect(() => {
		if (!initialUnsplashResults?.length) return;
		setIsLoading(false);
	}, [initialUnsplashResults]);

	const renderImageButton = (unsplashResult: Partial<UnsplashResult> | undefined) => {
		if (!unsplashResult) {
			return <ImagePlaceholder />;
		}

		const { fullSizeImageUrl, altDescription, smallSizeImageUrl } = unsplashResult;
		const isNoImage =
			!smallSizeImageUrl && !fullSizeImageUrl && fg('company_hub_manual_cards_refinement');
		const isSelectedImage = fullSizeImageUrl === selectedImageUrl || (isNoImage && hasNoImage);

		return (
			<Image
				key={unsplashResult.id || 'unsplash-no-image'}
				// default images have a thumbnail property, everything else does not
				imageSrc={smallSizeImageUrl ?? fullSizeImageUrl ?? ''}
				isSelectedImage={isSelectedImage}
				aria-label={altDescription || intl.formatMessage(i18n.noImageLabel)}
				isUploadingImage={isUploadingImage}
				onClick={() => onImageClick(unsplashResult)}
				isNoImage={isNoImage}
			>
				{isSelectedImage && <CheckboxIcon label="checkmark" color={token('color.icon.brand')} />}
			</Image>
		);
	};

	return (
		<Flex xcss={ImagesWrapperStyles} testId="default-image-picker">
			{isLoading || isDisabled
				? renderImagePlaceholders()
				: defaultUnsplashImages.map(renderImageButton)}
		</Flex>
	);
};
